<script lang="ts" setup>
import type { PreviewItemData } from '@autobid/strapi-integration/typescript/auction/carPreview'
import type { AuctionCar } from '@autobid/ui/types/Car'
import { useProfile } from '@autobid/ui/composables/useProfile'
import { useTransportOperator } from '@autobid/ui/composables/useTransportOperator'
import { computed, onServerPrefetch } from 'vue'

interface Props {
  car: AuctionCar | PreviewItemData
}

const props = defineProps<Props>()

const { data: profile, suspense: profileSuspense } = useProfile()
const { data: transportOperator, suspense } = useTransportOperator()

const transportType = computed<null | 'gefco' | 'ceva'>(() => {
  if (!profile.value?.extendedData?.addressBook || !transportOperator.value) {
    return null
  }

  const deliveryId
    = profile.value.extendedData.addressBook.vehicleDeliveryAddress
  const deliveryItem = profile.value.extendedData.addressBook.items[deliveryId]

  const transportEnabled = profile.value.parameters?.transport_enabled === '1'

  if (!transportEnabled) {
    return null
  }

  const isGefco = transportOperator.value.gefco.includes(deliveryItem.country.id)
  if (isGefco) {
    return props.car.status.transportAvailable ? 'gefco' : null
  }

  const isCeva = transportOperator.value.ceva.includes(deliveryItem.country.id)
  if (isCeva) {
    return props.car.status.transportCrossborderAvailable ? 'ceva' : null
  }

  return null
})

onServerPrefetch(async () => {
  await Promise.all([profileSuspense(), suspense()])
})
</script>

<template>
  <DynamicAuctionItemListCarActionsTransportCeva
    v-if="transportType === 'ceva'"
    :car="car"
  />

  <DynamicAuctionItemListCarActionsTransportGefco
    v-else-if="transportType === 'gefco'"
    :car="car"
  />
</template>
